export const C = {
  sage: '#5B7B5E', sageLight: '#EBF1EB', sageMid: '#A8C4AA',
  warm: '#F7F3EE', warmDark: '#EDE7DE', warmDeep: '#D4C9B8',
  stone: '#6B6560', stoneMid: '#9B9490',
  ink: '#2C2825', cream: '#FDFAF7',
  blush: '#E8A598', blushLight: '#FAEAE7',
  gold: '#C4954A', goldLight: '#FAF0DC',
  sky: '#6B9BAE', skyLight: '#E3EFF4',
  lavender: '#8B7BAE', lavenderLight: '#EDE9F5',
  coral: '#D4735A', coralLight: '#FAEAE5',
  mint: '#5A9B84', mintLight: '#E3F2EC',
  amber: '#E5A020', amberLight: '#FEF3DC',
  navy: '#2E4057', navyLight: '#E8EDF3',
};

// ── PILL COLOUR DERIVATION ────────────────────────────────────────────────────
// A custom category stores one colour. The pill paints text in that colour on a
// 15% tint of the same hue (hex + '26' alpha), so a single pick yields both
// halves of a legible pill without asking for a second colour.
export function tintColor(hex) {
  const h = String(hex || '').trim();
  return /^#[0-9a-f]{6}$/i.test(h) ? `${h}26` : '#EBF1EB';
}

function rgb(hex) {
  const h = String(hex || '').trim();
  if (!/^#[0-9a-f]{6}$/i.test(h)) return null;
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}

// Text sitting on a pale tint of its own hue goes unreadable once the hue itself
// is light. Above 0.4 relative luminance, mix the colour 60% toward black so it
// keeps its identity but regains contrast; darker hues are already legible.
export function pillTextColor(hex) {
  const c = rgb(hex);
  if (!c) return hex;
  const [r, g, b] = c;
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  if (lum <= 0.4) return hex;
  return '#' + c.map(v => Math.floor(v * 0.4).toString(16).padStart(2, '0')).join('');
}

// ── CATEGORY TYPE ─────────────────────────────────────────────────────────────
// Income used to be inferred from a positive amount, which is wrong in both
// directions: a refund makes an expense category positive (197 rows across 16
// categories today), and it makes a custom income category impossible to
// express at all. Type is declared, not inferred.
//
// 'transfer' is a real third case, not a dodge. Property Sale (+$190,731) and
// Loan Repayments (-$90,000) are balance-sheet movements; typing either as
// income or expense would swamp every real month in whichever total it landed
// in. They are already kept out of the app's figures by transfer_hide — the
// type records WHY rather than forcing a wrong answer.
//
// Entries carry no `type` key by default; the helper reads that as 'expense',
// so only the exceptions below are annotated.
export const CAT_TYPES = ['expense', 'income', 'transfer', 'gift'];

// ── HOUSEHOLD CATEGORIES ──────────────────────────────────────────────────────
export const CATS = {
  'Groceries':           { color: C.mint,     light: C.mintLight,     icon: '🛒', bucket: 'needs'   },
  'Dining Out':          { color: C.blush,    light: C.blushLight,    icon: '🍽️', bucket: 'wants'  },
  'Coffee & Treats':     { color: C.gold,     light: C.goldLight,     icon: '☕', bucket: 'wants'   },
  'Housing':             { color: C.sage,     light: C.sageLight,     icon: '🏠', bucket: 'needs'   },
  'Transportation':      { color: C.sky,      light: C.skyLight,      icon: '🚗', bucket: 'needs'   },
  'Gas':                 { color: C.sky,      light: C.skyLight,      icon: '⛽', bucket: 'needs'   },
  'Health':              { color: C.coral,    light: C.coralLight,    icon: '❤️', bucket: 'needs'  },
  'Shopping':            { color: C.lavender, light: C.lavenderLight, icon: '🛍️', bucket: 'wants' },
  'Household Supplies':  { color: C.mint,     light: C.mintLight,     icon: '🧴', bucket: 'needs'   },
  'Gifts & Donations':   { color: C.blush,    light: C.blushLight,    icon: '🎁', bucket: 'needs'   },
  'Savings & Investing': { color: C.sage,     light: C.sageLight,     icon: '💰', bucket: 'savings' },
  'Entertainment':       { color: C.gold,     light: C.goldLight,     icon: '🎬', bucket: 'wants'   },
  'Travel':              { color: C.sky,      light: C.skyLight,      icon: '✈️', bucket: 'wants'  },
  'Family & Kids':       { color: C.lavender, light: C.lavenderLight, icon: '👨‍👩‍👧', bucket: 'needs'},
  'Pets':                { color: C.mint,     light: C.mintLight,     icon: '🐾', bucket: 'wants'   },
  'Subscriptions':       { color: C.gold,     light: C.goldLight,     icon: '🔄', bucket: 'wants'   },
  'Utilities & Bills':   { color: C.stone,    light: C.warmDark,      icon: '💡', bucket: 'needs'   },
  'Bank Fees':           { color: C.stone,    light: C.warmDark,      icon: '🏦', bucket: 'wants'   },
  'Debt Payments':       { color: C.coral,    light: C.coralLight,    icon: '💳', bucket: 'savings' },
  'Income':              { color: C.sage,     light: C.sageLight,     icon: '💵', type: 'income' },
  'Uncategorized':       { color: C.stoneMid, light: C.warmDark,      icon: '❓', bucket: 'wants'   },
  // Hidden — no bucket; transfer type excludes them from all bucket calculations
  'Internal Transfer':   { color: C.stoneMid, light: C.warmDark,      icon: '↔️', hidden: true, type: 'transfer' },
  'Credit Card Payment': { color: C.stoneMid, light: C.warmDark,      icon: '💳', hidden: true, type: 'transfer' },
  'Loan Repayments':     { color: C.sky,      light: C.skyLight,      icon: '🤝', hidden: true, type: 'transfer' },
  'Property Sale':       { color: C.gold,     light: C.goldLight,     icon: '🏡', hidden: true, type: 'transfer' },
};

// ── BUSINESS CATEGORIES (Chase MM Studios) ────────────────────────────────────
export const BIZ_CATS = {
  'Software & Subscriptions': { color: C.navy,    light: C.navyLight,     icon: '💻', bucket: 'needs'   },
  'Advertising & Marketing':  { color: C.lavender,light: C.lavenderLight, icon: '📣', bucket: 'needs'   },
  'Meals & Entertainment':    { color: C.blush,   light: C.blushLight,    icon: '🍽️', bucket: 'wants'  },
  'Travel (Business)':        { color: C.sky,     light: C.skyLight,      icon: '✈️', bucket: 'needs'  },
  'Professional Fees':        { color: C.gold,    light: C.goldLight,     icon: '📋', bucket: 'needs'   },
  'Bank Fees':                { color: C.stone,   light: C.warmDark,      icon: '🏦', bucket: 'wants'   },
  'Equipment & Gear':         { color: C.sage,    light: C.sageLight,     icon: '🎛️', bucket: 'needs' },
  'Office & Supplies':        { color: C.mint,    light: C.mintLight,     icon: '📦', bucket: 'needs'   },
  'Payroll & Contractors':    { color: C.sage,    light: C.sageLight,     icon: '👥', bucket: 'needs'   },
  'Health (Business)':        { color: C.coral,   light: C.coralLight,    icon: '❤️', bucket: 'needs'  },
  'Gas & Transportation':     { color: C.sky,     light: C.skyLight,      icon: '⛽', bucket: 'needs'   },
  'Gifts & Donations':        { color: C.blush,   light: C.blushLight,    icon: '🎁', bucket: 'needs'   },
  'Business Income':          { color: C.sage,    light: C.sageLight,     icon: '💵', type: 'income' },
  'Uncategorized (Business)': { color: C.stoneMid,light: C.warmDark,      icon: '❓', bucket: 'wants'   },
  // Hidden — no bucket; transfer type excludes them from all bucket calculations
  'Credit Card Payment': { color: C.stoneMid, light: C.warmDark, icon: '💳', hidden: true, type: 'transfer' },
  'Internal Transfer':   { color: C.stoneMid, light: C.warmDark, icon: '↔️', hidden: true, type: 'transfer' },
};

// Fallback business set for isBusinessAccount() — used ONLY when the accounts
// registry is empty or unreachable. The registry is the source of truth;
// household is derived as "not business", so there is no household constant to
// keep in sync (a stale one silently drops registry-household accounts — see
// TALLY_RULES "Analysis traps"). Query the registry, never a hardcoded list.
export const BUSINESS_ACCOUNTS = ['Chase MM Studios (Visa)'];

export const VISIBLE_CATS = Object.entries(CATS).filter(([,v]) => !v.hidden).map(([k]) => k);
export const VISIBLE_BIZ_CATS = Object.entries(BIZ_CATS).filter(([,v]) => !v.hidden).map(([k]) => k);
export const ALL_CATS = Object.keys(CATS);
export const ALL_BIZ_CATS = Object.keys(BIZ_CATS);

export function getCat(name, mode = 'household', customCats = []) {
  if (mode === 'business' && BIZ_CATS[name]) return BIZ_CATS[name];
  if (CATS[name]) return CATS[name];
  if (BIZ_CATS[name]) return BIZ_CATS[name];
  const custom = customCats.find(c => c.name === name);
  if (custom) return { color: custom.color, light: custom.light_color, icon: custom.icon,
                       type: custom.type || 'expense', bucket: custom.bucket || 'wants' };
  return CATS['Uncategorized'] || { color: C.stoneMid, light: C.warmDark, icon: '❓' };
}

// Derived through getCat rather than repeating its lookup, so the two can never
// disagree about which map a name resolves against.
export function catType(name, mode = 'household', customCats = []) {
  const t = getCat(name, mode, customCats).type;
  return CAT_TYPES.includes(t) ? t : 'expense';
}
export const isIncomeCat  = (n, m, c) => catType(n, m, c) === 'income';
export const isExpenseCat = (n, m, c) => catType(n, m, c) === 'expense';
// Money in that is neither earned nor a self-transfer — gifts, personal
// e-transfers in, one-off personal-item sales. Excluded from the Income KPI, the
// trend line (income there is earned only), AND Cashflow (which is operating
// cashflow: earned income − spending). Still shows in the transaction list and
// account balances — it arrived, it just isn't operations.
export const isGiftCat    = (n, m, c) => catType(n, m, c) === 'gift';

// The exact row-level predicates behind the Income and Spending KPI cards,
// extracted once so a click-to-audit transaction filter can never drift from
// the number it's auditing — both the dashboard's sum and the transaction
// list's filter call these, not a reimplementation of the same logic.
// transfer_hide is baked in here rather than left to the caller: the KPI is
// computed over rows that already exclude transfer_hide (the dashboard's txs
// prop is pre-filtered), and a filtered list built from the same source array
// PLUS a "Show hidden" toggle must not silently pull in rows the KPI never
// counted. Redundant when called on already-hidden-excluded data, load-bearing
// when called on a list that might include hidden rows.
export const isKpiIncomeRow = (t, m, c) => !t.transfer_hide && isIncomeCat(t.category, m, c);
export const isKpiSpendingRow = (t, m, c) => !t.transfer_hide && parseFloat(t.amount) < 0
  && !isIncomeCat(t.category, m, c) && !isGiftCat(t.category, m, c);

export const BUCKETS = ['needs', 'wants', 'savings'];
export const BUCKET_LABELS = { needs: 'Needs', wants: 'Wants', savings: 'Savings' };

// Returns the effective bucket for a category, or null for income/transfer types.
// Uses getCat() as the single source of truth — custom categories now carry bucket
// in the object returned by getCat(), so there is no parallel lookup.
export function catBucket(name, mode, customCats) {
  const t = catType(name, mode, customCats);
  if (t !== 'expense') return null;
  const cat = getCat(name, mode, customCats);
  return cat.bucket || 'wants';
}

// A custom category is offered in a view when it targets that view or both.
// A missing view means a pre-migration row read by a stale client — treat as both.
export function visibleCustomCats(customCats = [], mode = 'household') {
  return customCats.filter(c => !c.view || c.view === 'both' || c.view === mode);
}

// The two views spell their catch-all differently, and parsers.js writes the
// household spelling on every account, so a confirm guard must accept both.
export const UNCATEGORIZED_CATS = ['Uncategorized', 'Uncategorized (Business)'];
export function isUncategorized(cat) {
  return !cat || UNCATEGORIZED_CATS.includes(cat);
}

// The account registry is the source of truth once it has entries; the hard-coded
// arrays remain the fallback so an empty or unreachable registry behaves exactly
// as it did before.
export function isBusinessAccount(account, accounts = []) {
  const entry = accounts.find(a => a.display_name === account);
  if (entry) return entry.view === 'business';
  return BUSINESS_ACCOUNTS.includes(account);
}

export function fmt(n) {
  const a = Math.abs(n);
  const s = '$' + a.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? '-' + s : s;
}

export function fmtDate(ds) {
  // Postgres DATE columns arrive as full ISO timestamps, not bare YYYY-MM-DD.
  const s = typeof ds === 'string' ? ds.slice(0, 10)
                                   : new Date(ds).toISOString().slice(0, 10);
  return new Date(s + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

export function monthLabel(y, m) {
  return new Date(y, m, 1).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
}
